
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EditStaffDialog } from '@/components/staff/edit-staff-dialog';
import { DeleteStaffDialog } from '@/components/staff/delete-staff-dialog';
import { AssignUsersDialog } from '@/components/staff/assign-users-dialog';
import { LoginQRDialog } from '@/components/shared/login-qr-dialog';
import {
  MoreHorizontalIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  User,
  ChevronDown,
  ChevronRight,
  UserMinus,
  ChevronLeft,
  QrCode,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useStaff, useRemoveUserFromStaff } from '@/api/staff';
import { useGym } from '@/contexts/gym-context';
import { useToast } from '@/hooks/use-toast';

interface StaffTableProps {
  searchQuery: string;
  onRefresh?: () => void;
}

export function StaffTable({ searchQuery, onRefresh }: StaffTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use states for the staff being edited or deleted
  const [editingStaff, setEditingStaff] = useState<string | null>(null);
  const [deletingStaff, setDeletingStaff] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [assignUsersStaffId, setAssignUsersStaffId] = useState<string | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [qrStaff, setQrStaff] = useState<{ id: string; name: string } | null>(
    null
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { selectedGym } = useGym();

  const {
    data: staffMembers,
    isLoading,
    error,
    refetch,
  } = useStaff(selectedGym === 'all' ? undefined : selectedGym);
  const removeUserFromStaff = useRemoveUserFromStaff();

  // Filter staff based on search query with null checks
  const allFilteredStaff = staffMembers
    ? staffMembers.filter(
        (staff) =>
          (staff.name?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (staff.surname?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (staff.email?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (staff.userType?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          )
      )
    : [];

  const totalPages = Math.ceil(allFilteredStaff.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const filteredStaff = allFilteredStaff.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  const handleRefresh = () => {
    refetch();
    if (onRefresh) onRefresh();
  };

  // Handle staff deletion
  const handleDeleteStaff = useCallback(
    (staff: any) => {
      console.log('Delete staff clicked:', staff);

      // Check if staff is an owner
      if (staff.userType === 'owner') {
        toast({
          title: t('common.error'),
          description: t('staff.cannotDeleteOwner'),
          variant: 'destructive',
        });
        return;
      }

      const staffId = staff._id || staff.id;
      const staffName = `${staff.name || ''} ${staff.surname || ''}`.trim();

      console.log('Opening delete dialog for staff:', staffName, staffId);
      setDeletingStaff({ id: staffId, name: staffName });
    },
    [t, toast]
  );

  const toggleRow = (staffId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [staffId]: !prev[staffId],
    }));
  };

  const handleRemoveUserFromStaff = (staffId: string, userId: string) => {
    removeUserFromStaff.mutate(
      { staffId, userId },
      {
        onSuccess: () => {
          // Optimistically update UI: refetch staff or update local state
          handleRefresh();
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-12 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-500">
        <p>{t('staff.errorLoading')}</p>
        <Button onClick={() => refetch()} variant="outline" className="mt-2">
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!staffMembers || staffMembers.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{t('staff.noStaff')}</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead />
            <TableHead>{t('staff.name')}</TableHead>
            <TableHead>{t('staff.userType')}</TableHead>
            <TableHead>{t('staff.email')}</TableHead>
            <TableHead>{t('staff.phone')}</TableHead>
            <TableHead>{t('staff.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredStaff.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center h-24">
                {t('staff.noSearchResults')}
              </TableCell>
            </TableRow>
          ) : (
            filteredStaff.map((staff) => {
              const staffId = String(staff._id || staff.id || '');
              return (
                <>
                  <TableRow key={staffId}>
                    <TableCell className="w-8">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleRow(staffId)}
                        aria-label={
                          expandedRows[staffId] ? 'Collapse row' : 'Expand row'
                        }
                      >
                        {expandedRows[staffId] ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">{`${staff.name || ''} ${
                      staff.surname || ''
                    }`}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {staff.userType === 'admin'
                          ? t('staff.userTypes.admin')
                          : staff.userType === 'reception'
                          ? t('staff.userTypes.reception')
                          : staff.userType === 'trainer'
                          ? t('staff.userTypes.trainer')
                          : staff.userType === 'owner'
                          ? t('staff.userTypes.owner')
                          : staff.userType}
                      </Badge>
                    </TableCell>
                    <TableCell>{staff.email || ''}</TableCell>
                    <TableCell>{staff.phone || ''}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          staff.status === 'active' ? 'default' : 'secondary'
                        }
                      >
                        {staff.status === 'active'
                          ? t('staff.active')
                          : t('staff.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontalIcon className="h-4 w-4" />
                            <span className="sr-only">
                              {t('common.openMenu')}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`/staff/${staffId}`)}
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            {t('common.view')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setEditingStaff(staffId)}
                          >
                            <PencilIcon className="h-4 w-4 mr-2" />
                            {t('common.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setAssignUsersStaffId(staffId)}
                          >
                            <User className="h-4 w-4 mr-2" />
                            {t('Assign Users')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setQrStaff({
                                id: staffId,
                                name: `${staff.name} ${staff.surname}`,
                              })
                            }
                          >
                            <QrCode className="h-4 w-4 mr-2" />
                            {t('qrCode.generateLoginQR')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteStaff(staff)}
                          >
                            <TrashIcon className="h-4 w-4 mr-2" />
                            {t('common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  {expandedRows[staffId] && (
                    <TableRow
                      key={`${staffId}-expanded`}
                      className="bg-muted/50"
                    >
                      <TableCell colSpan={7} className="p-0">
                        <div className="p-4">
                          <h4 className="text-sm font-medium mb-2">
                            {t('planAssignments.assignedUsers')}
                          </h4>
                          {staff.users && staff.users.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>{t('users.name')}</TableHead>
                                  <TableHead>{t('users.email')}</TableHead>
                                  <TableHead>{t('users.status')}</TableHead>
                                  <TableHead className="text-right">
                                    {t('common.actions')}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {staff.users.map((user: any) => (
                                  <TableRow key={user._id || user.id}>
                                    <TableCell>{`${user.name} ${user.surname}`}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={
                                          user.status === 'active'
                                            ? 'default'
                                            : 'secondary'
                                        }
                                      >
                                        {user.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                          handleRemoveUserFromStaff(
                                            staffId,
                                            user._id || user.id
                                          )
                                        }
                                        disabled={removeUserFromStaff.isPending}
                                      >
                                        <UserMinus className="h-4 w-4" />
                                        <span className="sr-only">
                                          {t('planAssignments.removeUser')}
                                        </span>
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {t('planAssignments.noAssignedUsers')}
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })
          )}
        </TableBody>
      </Table>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {/* Left: total count + page-size selector */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {allFilteredStaff.length} {t('staff.totalStaff', { defaultValue: 'staff members' })}
          </span>
          <Select
            value={String(itemsPerPage)}
            onValueChange={(v) => setItemsPerPage(Number(v))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Right: page navigation */}
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              {t('common.previous')}
            </Button>

            {(() => {
              const pageGroupSize = 10;
              const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
              const groupStart = currentGroup * pageGroupSize + 1;
              const groupEnd = Math.min(groupStart + pageGroupSize - 1, totalPages);
              const buttons = [];

              if (groupStart > 1) {
                buttons.push(
                  <Button
                    key="prev-group"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(groupStart - 1)}
                    className="w-8 h-8 p-0"
                  >
                    ...
                  </Button>
                );
              }

              for (let p = groupStart; p <= groupEnd; p++) {
                buttons.push(
                  <Button
                    key={p}
                    variant={currentPage === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(p)}
                    className="w-8 h-8 p-0"
                  >
                    {p}
                  </Button>
                );
              }

              if (groupEnd < totalPages) {
                buttons.push(
                  <Button
                    key="next-group"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(groupEnd + 1)}
                    className="w-8 h-8 p-0"
                  >
                    ...
                  </Button>
                );
              }

              return buttons;
            })()}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              {t('common.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Edit Staff Dialog */}
      {editingStaff && (
        <EditStaffDialog
          staffId={editingStaff}
          open={!!editingStaff}
          onOpenChange={() => setEditingStaff(null)}
          onSuccess={handleRefresh}
        />
      )}

      {/* Delete Staff Dialog */}
      {deletingStaff && (
        <DeleteStaffDialog
          staffId={deletingStaff.id}
          staffName={deletingStaff.name}
          open={!!deletingStaff}
          onOpenChange={(open) => {
            if (!open) setDeletingStaff(null);
          }}
          onSuccess={handleRefresh}
        />
      )}

      {/* Assign Users Dialog */}
      {assignUsersStaffId && (
        <AssignUsersDialog
          staffId={assignUsersStaffId}
          open={!!assignUsersStaffId}
          onOpenChange={(open) => {
            if (!open) setAssignUsersStaffId(null);
          }}
          onSuccess={handleRefresh}
        />
      )}

      {/* QR Code Dialog */}
      {qrStaff && (
        <LoginQRDialog
          userId={qrStaff.id}
          userName={qrStaff.name}
          open={!!qrStaff}
          onOpenChange={(open) => {
            if (!open) setQrStaff(null);
          }}
        />
      )}
    </>
  );
}
